import React, { useState } from "react";
import {
    Box,
    Grid,
    TextField,
    MenuItem,
    Button,
    CircularProgress,
    Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SaveIcon from "@mui/icons-material/Save";
import toast from "react-hot-toast";
import configServ from "../../services/config";
import QualificationList from "./QualificationList";
export const appServiceName = process.env.REACT_APP_BACKEND_URL;

const QualificationForm = ({ companyId, employeeId }) => {
    // 1. COMPONENT STATES
    const [qualifications, setqualifications] = useState([]); // For future qualification list rendering if needed
    const [formData, setFormData] = useState({
        degree_name: "",
        university_name: "",
        degree_status: "completed", // Default database ENUM standard
        year: "",
        percentage: "",
    });

    const [documentFile, setDocumentFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [submitLoading, setSubmitLoading] = useState(false);

    // 2. INPUT CHANGE HANDLING
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear field error on typing
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    // 3. FILE SELECT & VALIDATION (JPG, JPEG, PNG ONLY)
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

        if (!allowedTypes.includes(file.type)) {
            setErrors((prev) => ({
                ...prev,
                document_file: "Only image formats (.jpg, .jpeg, .png) are allowed!",
            }));
            setDocumentFile(null);
            return;
        }

        // Size constraint limit if required (e.g., Max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            setErrors((prev) => ({
                ...prev,
                document_file: "File size should not exceed 2MB!",
            }));
            setDocumentFile(null);
            return;
        }

        setDocumentFile(file);
        setErrors((prev) => ({ ...prev, document_file: "" }));
    };

    // 4. FRONTEND FORM VALIDATIONS
    const validateForm = () => {
        let tempErrors = {};
        if (!formData.degree_name.trim()) tempErrors.degree_name = "Degree Name is required";
        if (!formData.university_name.trim()) tempErrors.university_name = "University Name is required";

        if (formData.degree_status === "completed") {
            if (!formData.year) {
                tempErrors.year = "Passing Year is required";
            } else if (isNaN(formData.year) || formData.year.length !== 4) {
                tempErrors.year = "Enter a valid 4-digit year";
            }

            if (!formData.percentage) {
                tempErrors.percentage = "Percentage is required";
            } else if (isNaN(formData.percentage) || formData.percentage < 0 || formData.percentage > 100) {
                tempErrors.percentage = "Enter valid percentage (0 - 100)";
            }
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    // 5. POST CONTEXT FORM DATA SUBMIT
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitLoading(true);

            // File sending triggers multi-part boundary stream payload
            const dataPayload = new FormData();
            dataPayload.append("degree_name", formData.degree_name);
            dataPayload.append("university_name", formData.university_name);
            dataPayload.append("degree_status", formData.degree_status);
            dataPayload.append("employee_id", employeeId || "");
            dataPayload.append("company_id", companyId || "");

            // Send metrics only if course is successfully finished
            if (formData.degree_status === "completed") {
                dataPayload.append("year", formData.year);
                dataPayload.append("percentage", formData.percentage);
            }

            if (documentFile) {
                dataPayload.append("document_file", documentFile);
            }

            const response = await fetch(`${appServiceName}/api/save-qualification`, {
                method: "POST",
                body: dataPayload, // Multi-part object (Headers will be auto-set by fetch)
            });

            const result = await response.json();

            if (result.status) {
                toast.success("Qualification details saved successfully!");
                // Reset form if create mode action completes
                getqualifications({ employee_id: employeeId });

                setFormData({ degree_name: "", university_name: "", degree_status: "completed", year: "", percentage: "" });
                setDocumentFile(null);

            } else {
                toast.error(result.message || "Failed to process data.");
            }
        } catch (error) {
            console.error("Submission backend processing error:", error);
            toast.error("Server submission failed.");
        } finally {
            setSubmitLoading(false);
        }
    };

    const getqualifications = async (data) => {
        try {
            const response = await configServ.getQualifications(data);
            if (response.status) {
                setqualifications(response.data);
            } else {
                setqualifications([]);
                toast.error(response.message || "Failed to fetch qualifications.");
            }
        } catch (error) {
            console.error("Error fetching qualifications:", error);
            toast.error("Server error while fetching qualifications.");
        }
    };

    useState(() => {
        if (employeeId) {
            getqualifications({ employee_id: employeeId });
        }
    }, [employeeId]);

    const handleDeleteQualification = async (id) => {
        try{
            const response = await configServ.deleteQualification(id,{ employee_id: employeeId });
            if(response.status){
                toast.success("Qualification deleted successfully!");
                getqualifications({ employee_id: employeeId });
            } else {
                toast.error(response.message || "Failed to delete qualification.");
            }
        } catch (error) {
            console.error("Error deleting qualification:", error);
            toast.error("Server error while deleting qualification.");
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2.5}>
                {/* Degree Title Text */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        name="degree_name"
                        value={formData.degree_name}
                        onChange={handleChange}
                        label="Degree Name"
                        placeholder="e.g. B.Tech in CSE, MCA"
                        error={!!errors.degree_name}
                        helperText={errors.degree_name}
                        variant="outlined"
                    />
                </Grid>

                {/* University Organization Name */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        name="university_name"
                        value={formData.university_name}
                        onChange={handleChange}
                        label="University / Board Name"
                        placeholder="e.g. Stanford University"
                        error={!!errors.university_name}
                        helperText={errors.university_name}
                        variant="outlined"
                    />
                </Grid>

                {/* Course Execution Status Dropdown */}
                <Grid item xs={12} sm={4}>
                    <TextField
                        select
                        fullWidth
                        name="degree_status"
                        label="Degree Status"
                        value={formData.degree_status}
                        onChange={handleChange}
                        variant="outlined"
                    >
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="pursuing">Pursuing</MenuItem>
                    </TextField>
                </Grid>

                {/* Graduation Timeline Matrix */}
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        name="year"
                        value={formData.year}
                        onChange={handleChange}
                        label="Passing Year"
                        placeholder="2024"
                        disabled={formData.degree_status === "pursuing"} // Smart disabled field
                        error={!!errors.year}
                        helperText={errors.year}
                        variant="outlined"
                    />
                </Grid>

                {/* Score Percentage Scale Evaluation */}
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        name="percentage"
                        value={formData.percentage}
                        onChange={handleChange}
                        label="Percentage scored"
                        placeholder="84.50"
                        disabled={formData.degree_status === "pursuing"} // Smart disabled field
                        error={!!errors.percentage}
                        helperText={errors.percentage}
                        variant="outlined"
                    />
                </Grid>

                {/* DOCUMENT EXTRACTION MODULE (JPG UPLOAD WRAPPER) */}
                <Grid item xs={12}>
                    <Box
                        sx={{
                            p: 2.5,
                            border: errors.document_file ? "1px dashed #d32f2f" : "1px dashed #cbd5e1",
                            borderRadius: "12px",
                            bgcolor: "#f8fafc",
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle2" fontWeight="600" color="#334155">
                                Upload Degree Certificate
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Supported formats: .jpg, .jpeg, .png (Max limit: 2MB)
                            </Typography>
                            {documentFile && (
                                <Typography variant="body2" color="success.main" fontWeight="600" sx={{ mt: 0.5 }}>
                                    Selected: {documentFile.name}
                                </Typography>
                            )}
                            {errors.document_file && (
                                <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                    {errors.document_file}
                                </Typography>
                            )}
                        </Box>

                        {/* Native input button click execution */}
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: "600", whiteSpace: "nowrap" }}
                        >
                            Choose Image
                            <input type="file" hidden accept="image/jpeg, image/jpg, image/png" onChange={handleFileChange} />
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            {/* FORM ACTION TRIGGER CONTROLS */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={submitLoading}
                    startIcon={submitLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{
                        borderRadius: "10px",
                        px: 4,
                        py: 1.2,
                        fontWeight: "600",
                        textTransform: "none",
                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
                    }}
                >
                    {submitLoading ? "Uploading Data..." : "Save Qualification"}
                </Button>
            </Box>
            <QualificationList qualifications={qualifications} onDelete={handleDeleteQualification} />
        </Box>
    );
};

export default QualificationForm;